import { useEffect, useState } from "react";
import type { SearchFocus } from "@/shared/hooks/useSearchFocus";
import { Icon } from "@iconify/react";
import { ThemedSegmented } from "@/shared/components/ThemedSegmented";
import { Skeleton } from "antd";
import { EmptyState } from "@/shared/components/EmptyState";
import { NotAvailable } from "@/shared/components/NotAvailable";
import type {
  AllergyRecord,
  DiagnosisRecord,
  ExamVitalsVisit,
  FamilyHistoryRecord,
  ImmunizationRecord,
  ProcedureRecord,
} from "@/features/patient/clinical-history/utils/adaptClinicalHistory";
import { cn } from "@/shared/lib/utils";
import { MedicalHistoryPanels } from "./MedicalHistoryPanels";

interface CompleteHistorySectionProps {
  allergies: AllergyRecord[];
  conditions: DiagnosisRecord[];
  procedures: ProcedureRecord[];
  immunizations?: ImmunizationRecord[];
  examAndVitals?: ExamVitalsVisit[];
  familyHistory: FamilyHistoryRecord[];
  isLoading?: boolean;
  self?: { name: string; meta: string; conditionCount: number };
  /** A header search result to open here (useSearchFocus.ts). */
  focus?: SearchFocus;
}

const tabItems = [
  { key: "medical", label: "Medical", icon: "lucide:stethoscope" },
  { key: "family", label: "Family", icon: "lucide:user" },
  { key: "social", label: "Social", icon: "lucide:share-2" },
] as const;

function initials(name: string) {
  return (
    name
      .split(/[\s,]+/)
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .slice(0, 3)
      .toUpperCase() || "?"
  );
}

type GenogramPerson = {
  relation: string;
  condition: string;
  ageAtOnset?: number;
  status: "living" | "deceased";
  notes: string;
};

function PersonCard({
  person,
  avatarText,
  className,
  muted = false,
  center = false,
}: {
  person: GenogramPerson;
  avatarText: string;
  className?: string;
  muted?: boolean;
  center?: boolean;
}) {
  const living = person.status === "living";
  return (
    <div
      className={cn(
        "absolute z-10 rounded-2xl border px-4 py-6 text-center shadow-sm",
        center ? "w-[220px] min-h-[175px]" : "w-[210px] min-h-[215px]",
        muted ? "border-[#E5E7EB] bg-[#F3F4F6]" : "border-[#E5E7EB] bg-white",
        center && "border-[#F36F8F] shadow-[0_10px_26px_rgba(243,111,143,0.20)]",
        className,
      )}
    >
      {!center && (
        <span className={cn("absolute right-4 top-4 text-xs font-bold", living ? "text-[#40C463]" : "text-[#9CA3AF]")}>
          {living ? "Living" : "Deceased"}
        </span>
      )}
      <div
        className={cn(
          "mx-auto flex items-center justify-center rounded-full text-xs font-extrabold !text-white",
          center ? "h-16 w-16" : "h-14 w-14",
          center ? "bg-[#E4002B]/50" : "bg-[#0A2947]",
        )}
      >
        {avatarText}
      </div>
      <div className="mt-4 flex items-center justify-center gap-1">
        <p className="text-xs font-extrabold text-[#0A2947]">{person.relation}</p>
      </div>
      {!center &&
        (person.condition ? (
          <span className="mt-2 inline-block rounded-full bg-[#9CA3AF] px-2.5 py-1 text-xs font-bold !text-white">
            {person.condition}
          </span>
        ) : (
          <p className="mt-2 text-xs">
            <NotAvailable label="No condition recorded" />
          </p>
        ))}
      {person.ageAtOnset != null && <p className="mt-3 text-sm text-[#9CA3AF]">Age {person.ageAtOnset}</p>}
      {person.notes && <p className="mt-2 text-sm leading-5 text-[#6B7280]">{person.notes}</p>}
      {center && (
        <span className="mt-3 inline-block rounded-full bg-[#E4002B]/50 px-3 py-1 text-xs font-bold !text-white">
          {person.condition}
        </span>
      )}
    </div>
  );
}

function PlaceholderCard({ relation, className }: { relation: string; className?: string }) {
  return (
    <EmptyState
      className={cn("absolute z-10 w-[210px] min-h-[215px] justify-center border-dashed bg-[#F9FAFB] shadow-none", className)}
      icon={<Icon icon="lucide:user" className="h-8 w-8 text-[#9CA3AF]" />}
      title={relation}
      description="Not on file"
    />
  );
}

const GENOGRAM_SLOTS: { key: string; className: string; muted?: boolean }[] = [
  { key: "Brother", className: "left-[20px] top-0" },
  { key: "Sister", className: "left-[20px] top-[390px]" },
  { key: "Father", className: "left-[340px] top-[205px]" },
  { key: "Mother", className: "left-[950px] top-[205px]" },
  { key: "Paternal Grandfather", className: "left-[1310px] top-0", muted: true },
  { key: "Maternal Grandmother", className: "left-[1310px] top-[390px]", muted: true },
];

export function CompleteHistorySection({
  allergies,
  conditions,
  procedures,
  immunizations = [],
  examAndVitals = [],
  familyHistory,
  isLoading = false,
  self,
  focus,
}: CompleteHistorySectionProps) {
  const [activeTab, setActiveTab] = useState<(typeof tabItems)[number]["key"]>("medical");
  // A header search result is a medical record: that tab opens it.
  useEffect(() => {
    if (focus) setActiveTab("medical");
  }, [focus]);
  const familyByRelation = Object.fromEntries(familyHistory.map((item) => [item.relationKey, item]));

  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="mb-7 flex justify-center">
        <ThemedSegmented
          selectedBg="#0A2947"
          itemColor="#6B7280"
          value={activeTab}
          onChange={(v) => setActiveTab(v as (typeof tabItems)[number]["key"])}
          options={tabItems.map(({ key, label, icon }) => ({
            value: key,
            label: (
              <span className="flex items-center gap-2 px-3 py-1 text-sm font-extrabold">
                <Icon icon={icon} className="h-4 w-4" />
                {label}
              </span>
            ),
          }))}
          className="!rounded-full !border-2 !border-[#9CA3AF] !shadow-sm [&_.ant-segmented-item]:!rounded-full [&_.ant-segmented-thumb]:!rounded-full"
        />
      </div>

      {activeTab === "medical" && (
        <MedicalHistoryPanels allergies={allergies} conditions={conditions} procedures={procedures} immunizations={immunizations} examAndVitals={examAndVitals} focus={focus} />
      )}

      {activeTab === "family" && (
        <section>
          <h2 className="mb-6 text-sm !font-extrabold text-[#0A2947]">Family Information:</h2>
          {familyHistory.length === 0 ? (
            <EmptyState
              icon={<Icon icon="lucide:users" className="h-12 w-12 text-[#9CA3AF]" />}
              title="No family history recorded"
              description="Family medical history from your records will appear here"
            />
          ) : (
            <div className="overflow-x-auto rounded-3xl bg-[#F7F7F7]">
              <div className="relative min-h-[640px] min-w-[1540px]">
                <div className="absolute left-[260px] top-[104px] h-[390px] w-px bg-[#D1D5DB]" />
                <div className="absolute left-[230px] top-[104px] h-px w-[30px] bg-[#D1D5DB]" />
                <div className="absolute left-[230px] top-[494px] h-px w-[30px] bg-[#D1D5DB]" />
                <div className="absolute left-[260px] top-[305px] h-px w-[80px] bg-[#D1D5DB]" />
                <div className="absolute left-[550px] top-[305px] h-px w-[90px] bg-[#D1D5DB]" />
                <div className="absolute left-[860px] top-[305px] h-px w-[90px] bg-[#D1D5DB]" />
                <div className="absolute left-[1160px] top-[305px] h-px w-[80px] bg-[#D1D5DB]" />
                <div className="absolute left-[1240px] top-[104px] h-[390px] w-px bg-[#D1D5DB]" />
                <div className="absolute left-[1240px] top-[104px] h-px w-[70px] bg-[#D1D5DB]" />
                <div className="absolute left-[1240px] top-[494px] h-px w-[70px] bg-[#D1D5DB]" />

                {GENOGRAM_SLOTS.map((slot) => {
                  const match = familyByRelation[slot.key];
                  if (!match) return <PlaceholderCard key={slot.key} relation={slot.key} className={slot.className} />;
                  return (
                    <PersonCard
                      key={slot.key}
                      muted={slot.muted}
                      className={slot.className}
                      avatarText={initials(match.relation)}
                      person={{
                        relation: match.relation,
                        condition: match.condition,
                        ageAtOnset: match.ageAtOnset,
                        status: match.status,
                        notes: match.notes,
                      }}
                    />
                  );
                })}

                <PersonCard
                  center
                  className="left-[640px] top-[245px]"
                  avatarText={initials(self?.name ?? "You")}
                  person={{
                    relation: self?.name ?? "You",
                    condition:
                      self && self.conditionCount > 0
                        ? `${self.conditionCount} health condition${self.conditionCount === 1 ? "" : "s"}`
                        : "No conditions on file",
                    status: "living",
                    notes: self?.meta || "",
                  }}
                />
              </div>
            </div>
          )}
        </section>
      )}

      {activeTab === "social" && (
        <section>
          <h2 className="mb-5 text-sm !font-extrabold text-[#0A2947]">Social Information:</h2>
          <EmptyState
            icon={<Icon icon="lucide:share-2" className="h-12 w-12 text-[#9CA3AF]" />}
            title="Social history not available"
            description="Tobacco, alcohol, exercise and other social history are not extracted from uploaded records yet."
          />
        </section>
      )}
    </div>
  );
}
